import { useEffect, useState } from "react";
import compareIcon from "../../assets/icons/compare-icon.svg";
import enlargeIcon from "../../assets/icons/enlarge-icon.svg";
import { useGraphs } from "../../context/GraphsProvider";
import Loader from "../AppLoader";
import Error from "../Error";
import Graph from "./Graph";
import SingleGraphView from "./GraphsGrid/SingleGraphView";
import { useGraphEnlarge } from "../../context/GraphEnlargeContext";

export default function AnaerobicGraphsGrid({
    onVatRelease,
    onRCPRelease,
    updateIsEnlarged,
    updateIsCompared,
}: {
    onVatRelease: (vat: number) => void;
    onRCPRelease?: (rcp: number) => void;
    updateIsEnlarged?: (isOnCompare: boolean) => void;
    updateIsCompared?: (isOnCompare: boolean) => void;
}) {
    const { graphs, loading, error } = useGraphs();
    const { graphIdToEnlarge, setGraphIdToEnlarge } = useGraphEnlarge();
    const [graphIdToCompare, setGraphIdToCompare] = useState<null | string>(
        null
    );

    useEffect(() => {
        if (updateIsEnlarged) {
            updateIsEnlarged(Boolean(graphIdToEnlarge));
        }
        if (updateIsCompared) {
            updateIsCompared(Boolean(graphIdToCompare));
        }
    }, [
        graphIdToCompare,
        graphIdToEnlarge,
        updateIsCompared,
        updateIsEnlarged,
    ]);

    if (loading)
        return (
            <div className="w-full grid place-content-center">
                <Loader />
            </div>
        );

    if (error) return <Error />;

    if (!graphs) return null;
    return graphIdToCompare ? (
        <SingleGraphView
            graphIdToEnlarge={graphIdToCompare}
            setGraphIdToEnlarge={setGraphIdToCompare}
            onVatRelease={onVatRelease}
            onRCPRelease={onRCPRelease}
            withoutMaxPoint
            compareMode
            isReverseVATColor={graphIdToCompare === "4"}
        />
    ) : graphIdToEnlarge ? (
        <div className="w-full h-full">
            <SingleGraphView
                graphIdToEnlarge={graphIdToEnlarge}
                setGraphIdToEnlarge={setGraphIdToEnlarge}
                onVatRelease={onVatRelease}
                onRCPRelease={onRCPRelease}
                withoutMaxPoint
                isReverseVATColor={graphIdToEnlarge === "4"}
            />
        </div>
    ) : (
        <div className="grid grid-cols-2 justify-between h-full gap-x-6 xl:gap-x-10 gap-y-10 xl:gap-y-11 2xl:gap-y-[18px] 2xl:gap-x-20 max-w-[950px] xl:max-w-[900px] 2xl:max-w-[1160px]">
            {graphs.map((graph, index) => (
                <div key={graph.graph_id}>
                    <div className="flex items-center gap-2 xl:gap-5 pb-1 xl:pb-2">
                        <h6 className="text-black mr-auto text-sm xl:text-xl font-bold">
                            {graph.title}
                        </h6>
                        <button
                            className={`size-5 xl:size-[30px]`}
                            onClick={() => {
                                setGraphIdToCompare(graph.graph_id);
                                setGraphIdToEnlarge(null);
                            }}
                        >
                            <img
                                src={compareIcon}
                                alt="Compare Icon"
                                className="w-full h-full"
                            />
                        </button>
                        <button
                            className={`size-5 xl:size-[30px]`}
                            onClick={() => {
                                setGraphIdToEnlarge(graph.graph_id);
                                setGraphIdToCompare(null);
                            }}
                        >
                            <img
                                src={enlargeIcon}
                                alt="Enlarge Icon"
                                className="w-full h-full"
                            />
                        </button>
                    </div>
                    <Graph
                        graphData={graph}
                        size="lg"
                        onVatRelease={onVatRelease}
                        onRCPRelease={onRCPRelease}
                        withoutMaxPoint
                        isReverseVATColor={index === 3}
                    />
                </div>
            ))}
        </div>
    );
}
